import {
    CollectedDropProps,
    dropConfigurationHandler,
    fileAllowableCheck,
    FileDropPayload,
} from "@nova/component-upload-dropbox"
import { FileExtension } from "@nova/constants"
import { useLoadingText } from "features/data-management/workspace/workspace/useLoadingText"
import { mapExtensionsToMimeType } from "features/job-management/uploader/constants"
import { ChangeEvent, useMemo, useRef, useState } from "react"
import { useDrop } from "react-dnd"

export type UseDropBoxProps = {
    canAcceptMultiple?: boolean
    allowedFileExtensions: FileExtension[]
    onNewItems: (files: File[]) => Promise<void>
    isProcessing: boolean
    processingTitle?: string
}

export const useDropBox = ({
    canAcceptMultiple = false,
    allowedFileExtensions,
    onNewItems,
    isProcessing,
    processingTitle = "Processing",
}: UseDropBoxProps) => {
    const uploadRef = useRef<HTMLInputElement>(null)
    const [hasUploadError, setHasUploadError] = useState(false)
    const [canDrop, setCanDrop] = useState(false)
    const processingText = useLoadingText(isProcessing, processingTitle)

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const files: File[] = Array.from(e.target.files || [])
        handleNewFiles(files)
    }

    const handleNewFiles = (files: File[]) => {
        const filteredFiles = fileAllowableCheck(
            files,
            allowedFileExtensions,
            mapExtensionsToMimeType
        )
        const fileIsAllowed = filteredFiles.length !== 0
        setHasUploadError(!fileIsAllowed)
        if (!fileIsAllowed) return

        onNewItems(filteredFiles)
    }

    /** For displaying which file types are allowed. */
    const fileTypeList = useMemo(
        () => allowedFileExtensions.map((type) => `.${type}`).join(", "),
        [allowedFileExtensions]
    )

    /** For the accepted values in the input element. */
    const formattedFileTypes = useMemo(
        () => allowedFileExtensions.map((type) => `.${type}`).join(","),
        [allowedFileExtensions]
    )

    const [{ isOver }, dropRef] = useDrop<FileDropPayload, void, CollectedDropProps>(
        dropConfigurationHandler(
            handleNewFiles,
            allowedFileExtensions,
            setHasUploadError,
            mapExtensionsToMimeType,
            undefined,
            canAcceptMultiple ? undefined : 1
        ),
        [handleNewFiles, allowedFileExtensions, setHasUploadError]
    )

    return {
        uploadRef,
        hasUploadError,
        canDrop,
        setCanDrop,
        processingText,
        handleInputChange,
        fileTypeList,
        formattedFileTypes,
        isOver,
        dropRef,
    }
}
